import React, { useCallback, useEffect, useRef, useState } from "react";
import "./SuperAdminDashboard.css";
import { NavLink, useNavigate, Outlet, Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import AuthUser from "../../Hooks/authUser";
import logo from "../../Assets/logo.png";
import { server_url } from "../../Config/API";

/* ─────────────────────────────────────────────────────────────────── */
/* Nav menu config                                                      */
/* ─────────────────────────────────────────────────────────────────── */
const menuGroups = [
  {
    title: "Overview",
    items: [
      { type: "link", path: "overview", label: "Dashboard", icon: "solar:widget-5-bold-duotone" },
    ],
  },
  {
    title: "Management",
    items: [
      { type: "link", path: "superadmin-nonreferraluser", label: "Non Referral Users", icon: "solar:user-cross-bold-duotone" },
      { type: "link", path: "superadmin-allresellerlist", label: "All Resellers", icon: "solar:users-group-two-rounded-bold-duotone" },
      { type: "link", path: "superadmin-alldoctorlist", label: "All Doctors", icon: "solar:medical-kit-bold-duotone" },
      { type: "link", path: "superadmin-allpatientlist", label: "All Patients", icon: "solar:user-heart-bold-duotone" },
    ],
  },
  {
    title: "Commerce",
    items: [
      {
        type: "dropdown", key: "products", label: "Products", icon: "solar:box-bold-duotone",
        subItems: [
          { path: "superadmin-productadd", label: "Add Product" },
          { path: "superadmin-addorderdetails", label: "Shipping & Tax" },
          { path: "superadmin-producttable", label: "All Products" },
        ],
      },
      {
        type: "dropdown", key: "orders", label: "Orders", icon: "solar:cart-large-bold-duotone",
        subItems: [
          { path: "superadmin-order", label: "All Orders" },
          { path: "superadmin-confirm-order", label: "Confirmed" },
          { path: "superadmin-reject-order", label: "Rejected" },
        ],
      },
      {
        type: "dropdown", key: "contact", label: "Messages", icon: "solar:letter-opened-bold-duotone",
        subItems: [
          { path: "superadmin-contactlist", label: "All Contacts" },
        ],
      },
    ],
  },
  {
    title: "Content",
    items: [
      {
        type: "dropdown", key: "frontend", label: "Frontend Editor", icon: "solar:window-frame-bold-duotone",
        subItems: [
          { path: "superadmin-teamform", label: "Team Members" },
          { path: "updateHero", label: "Hero Section" },
          { path: "updateHomeState", label: "Home Stats" },
          { path: "updateWhyMednyne", label: "Why Caresee" },
          { path: "updateDoctorReview", label: "Doctor Reviews" },
          { path: "updateGrow", label: "Grow Section" },
          { path: "updateSteps", label: "Steps" },
          { path: "updateLectures", label: "Lectures" },
          { path: "updatePatientReview", label: "Patient Reviews" },
          { path: "updatePlans", label: "Plans" },
          { path: "updateResearch", label: "Research" },
          { path: "updateBrochure", label: "Brochure" },
          { path: "updateUserFaq", label: "User FAQ" },
          { path: "updateDoctorFaq", label: "Doctor FAQ" },
          { path: "updateFooter", label: "Footer" },
        ],
      },
    ],
  },
  {
    title: "Account",
    items: [
      { type: "link", path: "changePassword", label: "Change Password", icon: "solar:key-bold-duotone" },
    ],
  },
];

/* ─────────────────────────────────────────────────────────────────── */
/* Notification helpers                                                 */
/* ─────────────────────────────────────────────────────────────────── */
const NOTIF_LAST_READ_KEY = "superAdminNotifLastRead";
const SETTINGS_KEY = "superAdminSettings";

const defaultSettings = {
  compactMode: false,
  autoRefresh: false,
  autoRefreshInterval: 5,
};

const notifTypeConfig = {
  warning: { icon: "solar:clock-circle-bold-duotone", color: "text-amber-500", bg: "bg-amber-50", border: "border-amber-100" },
  info:    { icon: "solar:user-plus-bold-duotone",    color: "text-blue-500",  bg: "bg-blue-50",  border: "border-blue-100"  },
  error:   { icon: "solar:close-circle-bold-duotone", color: "text-rose-500",  bg: "bg-rose-50",  border: "border-rose-100"  },
  success: { icon: "solar:check-circle-bold-duotone", color: "text-emerald-500", bg: "bg-emerald-50", border: "border-emerald-100" },
};

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

/* ─────────────────────────────────────────────────────────────────── */
/* Main component                                                       */
/* ─────────────────────────────────────────────────────────────────── */
const SuperAdminDashboard = () => {
  const { userInfo, logout } = AuthUser();
  const navigate = useNavigate();
  const navWrapper = useRef();

  const [issideNavOpen, setSidenavOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState("");

  // Notification panel
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const notifRef = useRef();

  // Settings panel
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(defaultSettings);
  const settingsRef = useRef();
  const autoRefreshTimer = useRef(null);

  const toggleMenu = (key) => setOpenMenu((prev) => (prev === key ? "" : key));
  const handleToggle = () => setSidenavOpen((prev) => !prev);

  /* Load settings from localStorage */
  useEffect(() => {
    try {
      const saved = localStorage.getItem(SETTINGS_KEY);
      if (saved) setSettings({ ...defaultSettings, ...JSON.parse(saved) });
    } catch (_) {}
  }, []);

  const saveSettings = (newSettings) => {
    setSettings(newSettings);
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(newSettings));
  };

  const toggleSetting = (key) => {
    const next = { ...settings, [key]: !settings[key] };
    saveSettings(next);
  };

  /* Auto-refresh: dispatch a window event that the dashboard listens for */
  useEffect(() => {
    if (autoRefreshTimer.current) clearInterval(autoRefreshTimer.current);
    if (settings.autoRefresh) {
      autoRefreshTimer.current = setInterval(() => {
        window.dispatchEvent(new CustomEvent("dashboardAutoRefresh"));
      }, settings.autoRefreshInterval * 60 * 1000);
    }
    return () => clearInterval(autoRefreshTimer.current);
  }, [settings.autoRefresh, settings.autoRefreshInterval]);

  /* Close sidenav on outside click */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (issideNavOpen && navWrapper.current && !navWrapper.current.contains(e.target)) {
        setSidenavOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [issideNavOpen]);

  /* Close notification panel on outside click */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifOpen && notifRef.current && !notifRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [notifOpen]);

  /* Close settings panel on outside click */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (settingsOpen && settingsRef.current && !settingsRef.current.contains(e.target)) {
        setSettingsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [settingsOpen]);

  /* Fetch notifications */
  const fetchNotifications = useCallback(async () => {
    setNotifLoading(true);
    try {
      const token = localStorage.getItem("careSeeAccessToken");
      const lastRead = localStorage.getItem(NOTIF_LAST_READ_KEY) || "";
      const url = `${server_url}/user/notifications${lastRead ? `?lastReadAt=${encodeURIComponent(lastRead)}` : ""}`;
      const res = await fetch(url, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { authorization: `Bearer ${token}` } : {}),
        },
      });
      const json = await res.json();
      if (json.status === "success") {
        setNotifications(json.data.notifications || []);
        setUnreadCount(json.data.unreadCount || 0);
      }
    } catch (_) {
      /* fail silently */
    } finally {
      setNotifLoading(false);
    }
  }, []);

  /* Fetch unread count on mount */
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const openNotifPanel = () => {
    setNotifOpen(true);
    setSettingsOpen(false);
    fetchNotifications();
  };

  const markAllRead = () => {
    localStorage.setItem(NOTIF_LAST_READ_KEY, new Date().toISOString());
    setUnreadCount(0);
  };

  const openSettingsPanel = () => {
    setSettingsOpen((p) => !p);
    setNotifOpen(false);
  };

  /* ── Sidebar nav renderer ── */
  const renderNavItem = (item, idx) => {
    if (item.type === "link") {
      return (
        <li key={idx} onClick={() => setSidenavOpen(false)}>
          <NavLink
            to={item.path}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 text-[13px] font-semibold rounded-xl transition-all duration-200 ${
                isActive
                  ? "bg-cyan-500/15 text-cyan-400 border border-cyan-500/20 shadow-sm"
                  : "text-slate-500 hover:text-slate-200 hover:bg-white/[0.05]"
              }`
            }
          >
            <Icon icon={item.icon} className="text-[18px] flex-shrink-0" />
            <span className="flex-1">{item.label}</span>
          </NavLink>
        </li>
      );
    }

    if (item.type === "dropdown") {
      const isOpen = openMenu === item.key;
      return (
        <li key={idx}>
          <button
            onClick={() => toggleMenu(item.key)}
            className={`flex items-center justify-between w-full px-3 py-2.5 text-[13px] font-semibold rounded-xl transition-all duration-200 ${
              isOpen ? "bg-white/[0.06] text-slate-200" : "text-slate-500 hover:text-slate-200 hover:bg-white/[0.05]"
            }`}
          >
            <div className="flex items-center gap-3">
              <Icon icon={item.icon} className="text-[18px] flex-shrink-0" />
              <span>{item.label}</span>
            </div>
            <Icon
              icon="solar:alt-arrow-down-bold"
              className={`text-slate-600 text-sm transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
            />
          </button>
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              isOpen ? "max-h-[700px] opacity-100" : "max-h-0 opacity-0 pointer-events-none"
            }`}
          >
            <ul className="ml-5 mt-1 mb-2 pl-3 border-l border-slate-800 space-y-0.5">
              {item.subItems.map((sub, subIdx) => (
                <li key={subIdx} onClick={() => setSidenavOpen(false)}>
                  <NavLink
                    to={sub.path}
                    className={({ isActive }) =>
                      `flex items-center gap-2 px-3 py-2 text-[12px] font-medium rounded-lg transition-all duration-150 ${
                        isActive
                          ? "text-cyan-400 bg-cyan-500/10"
                          : "text-slate-600 hover:text-slate-200 hover:bg-white/[0.05]"
                      }`
                    }
                  >
                    <span className="w-1 h-1 rounded-full bg-current flex-shrink-0 opacity-50" />
                    {sub.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        </li>
      );
    }
    return null;
  };

  /* ── Sidebar content ── */
  const SidebarContent = () => (
    <div className="flex flex-col h-full select-none">
      <div className="px-5 pt-6 pb-5">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-lg shadow-cyan-500/30 flex-shrink-0">
            <Icon icon="solar:health-bold-duotone" className="text-white text-xl" />
          </div>
          <div>
            <p className="text-[14px] font-black text-white tracking-tight leading-none">Caresee</p>
            <p className="text-[10px] text-slate-600 font-semibold mt-0.5 tracking-wider uppercase">Admin Suite</p>
          </div>
        </Link>
      </div>

      <div className="mx-4 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />

      <div className="flex-1 overflow-y-auto py-4 px-3 space-y-5 sidebar-scroll">
        {menuGroups.map((group, gIdx) => (
          <div key={gIdx}>
            <p className="text-[9px] font-black text-slate-700 uppercase tracking-[0.18em] px-3 mb-2">{group.title}</p>
            <ul className="space-y-0.5">
              {group.items.map((item, idx) => renderNavItem(item, idx))}
            </ul>
          </div>
        ))}
      </div>

      <div className="mx-4 h-px bg-gradient-to-r from-transparent via-slate-800 to-transparent" />

      <div className="p-4">
        <div className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.04] border border-white/[0.06] hover:bg-white/[0.07] transition-colors cursor-default">
          <div className="relative flex-shrink-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white font-black flex items-center justify-center text-sm shadow-lg shadow-indigo-500/20">
              {(userInfo?.name || "SA").substring(0, 2).toUpperCase()}
            </div>
            <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 border-2 border-[#080f1e] rounded-full" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] font-bold text-slate-200 truncate leading-tight">{userInfo?.name || "Super Admin"}</p>
            <p className="text-[10px] text-slate-600 truncate mt-0.5">Owner Access</p>
          </div>
          <button
            onClick={() => { setSidenavOpen(false); logout(); }}
            className="text-slate-700 hover:text-rose-400 transition-colors p-1.5 rounded-lg hover:bg-rose-500/10 flex-shrink-0"
            title="Logout"
          >
            <Icon icon="solar:logout-bold-duotone" className="text-lg" />
          </button>
        </div>
      </div>
    </div>
  );

  /* ── Notification panel ── */
  const NotificationPanel = () => (
    <div className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/60 z-50 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="text-[14px] font-black text-slate-800">Notifications</h3>
          <p className="text-[11px] text-slate-400 font-medium mt-0.5">
            {unreadCount > 0 ? `${unreadCount} pending actions` : "All caught up!"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              className="text-[11px] font-bold text-cyan-600 hover:text-cyan-700 hover:bg-cyan-50 px-2.5 py-1 rounded-lg transition-colors"
            >
              Mark all read
            </button>
          )}
          <button
            onClick={() => setNotifOpen(false)}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <Icon icon="solar:close-square-bold-duotone" className="text-lg" />
          </button>
        </div>
      </div>

      <div className="max-h-[420px] overflow-y-auto divide-y divide-slate-50">
        {notifLoading ? (
          <div className="py-10 flex flex-col items-center justify-center gap-3">
            <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-[12px] text-slate-400 font-medium">Loading notifications...</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="py-12 flex flex-col items-center justify-center gap-2 text-slate-300">
            <Icon icon="solar:bell-off-bold-duotone" className="text-4xl" />
            <p className="text-[12px] font-medium text-slate-400">No notifications</p>
          </div>
        ) : (
          notifications.map((n) => {
            const cfg = notifTypeConfig[n.type] || notifTypeConfig.info;
            return (
              <div
                key={n.id}
                className="flex items-start gap-3 px-5 py-3.5 hover:bg-slate-50 transition-colors cursor-pointer"
                onClick={() => { setNotifOpen(false); navigate(n.link || "#"); }}
              >
                <div className={`w-9 h-9 rounded-xl ${cfg.bg} border ${cfg.border} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <Icon icon={cfg.icon} className={`${cfg.color} text-lg`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[12px] font-bold text-slate-800 leading-tight">{n.title}</p>
                  <p className="text-[11px] text-slate-500 font-medium mt-0.5 leading-snug">{n.message}</p>
                  <p className="text-[10px] text-slate-400 font-medium mt-1">{timeAgo(n.createdAt)}</p>
                </div>
                <Icon icon="solar:arrow-right-bold" className="text-slate-300 text-sm flex-shrink-0 mt-1" />
              </div>
            );
          })
        )}
      </div>

      <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/80">
        <button
          onClick={() => { setNotifOpen(false); fetchNotifications(); }}
          className="w-full text-center text-[11px] font-bold text-slate-500 hover:text-slate-700 py-1 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-center gap-2"
        >
          <Icon icon="solar:refresh-bold-duotone" className="text-sm" />
          Refresh notifications
        </button>
      </div>
    </div>
  );

  /* ── Settings panel ── */
  const SettingsPanel = () => (
    <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/60 z-50 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div>
          <h3 className="text-[14px] font-black text-slate-800">Dashboard Settings</h3>
          <p className="text-[11px] text-slate-400 font-medium mt-0.5">Customize your experience</p>
        </div>
        <button
          onClick={() => setSettingsOpen(false)}
          className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <Icon icon="solar:close-square-bold-duotone" className="text-lg" />
        </button>
      </div>

      <div className="p-4 space-y-1">
        {/* Compact mode */}
        <div className="flex items-center justify-between p-3.5 rounded-xl hover:bg-slate-50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
              <Icon icon="solar:minimize-square-bold-duotone" className="text-indigo-500 text-base" />
            </div>
            <div>
              <p className="text-[12px] font-bold text-slate-700">Compact Mode</p>
              <p className="text-[10px] text-slate-400 font-medium">Reduce page spacing</p>
            </div>
          </div>
          <button
            onClick={() => toggleSetting("compactMode")}
            className={`relative w-10 h-5.5 rounded-full transition-colors duration-200 focus:outline-none ${settings.compactMode ? "bg-indigo-500" : "bg-slate-200"}`}
            style={{ height: "22px", width: "40px" }}
          >
            <span
              className={`absolute top-0.5 w-4.5 h-4.5 bg-white rounded-full shadow transition-transform duration-200 ${settings.compactMode ? "translate-x-[18px]" : "translate-x-0.5"}`}
              style={{ width: "18px", height: "18px" }}
            />
          </button>
        </div>

        {/* Auto-refresh */}
        <div className="flex items-center justify-between p-3.5 rounded-xl hover:bg-slate-50 transition-colors">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
              <Icon icon="solar:refresh-bold-duotone" className="text-emerald-500 text-base" />
            </div>
            <div>
              <p className="text-[12px] font-bold text-slate-700">Auto Refresh</p>
              <p className="text-[10px] text-slate-400 font-medium">Refresh data automatically</p>
            </div>
          </div>
          <button
            onClick={() => toggleSetting("autoRefresh")}
            className={`relative rounded-full transition-colors duration-200 focus:outline-none ${settings.autoRefresh ? "bg-emerald-500" : "bg-slate-200"}`}
            style={{ height: "22px", width: "40px" }}
          >
            <span
              className={`absolute top-0.5 w-4.5 h-4.5 bg-white rounded-full shadow transition-transform duration-200 ${settings.autoRefresh ? "translate-x-[18px]" : "translate-x-0.5"}`}
              style={{ width: "18px", height: "18px" }}
            />
          </button>
        </div>

        {/* Interval selector - only visible when auto-refresh is on */}
        {settings.autoRefresh && (
          <div className="mx-1 p-3 rounded-xl bg-emerald-50 border border-emerald-100">
            <p className="text-[11px] font-bold text-emerald-700 mb-2">Refresh interval</p>
            <div className="flex gap-2">
              {[1, 5, 10].map((min) => (
                <button
                  key={min}
                  onClick={() => saveSettings({ ...settings, autoRefreshInterval: min })}
                  className={`flex-1 py-1.5 rounded-lg text-[11px] font-bold transition-all ${
                    settings.autoRefreshInterval === min
                      ? "bg-emerald-600 text-white shadow-sm"
                      : "bg-white text-slate-600 border border-emerald-200 hover:bg-emerald-100"
                  }`}
                >
                  {min}m
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="px-5 py-3 border-t border-slate-100 bg-slate-50/80">
        <button
          onClick={() => { saveSettings(defaultSettings); }}
          className="w-full text-center text-[11px] font-bold text-slate-500 hover:text-rose-600 py-1 rounded-lg hover:bg-rose-50 transition-colors"
        >
          Reset to defaults
        </button>
      </div>
    </div>
  );

  return (
    <section className={`bg-[#f6f8fc] min-h-screen ${settings.compactMode ? "compact-mode" : ""}`}>
      <div className="flex">
        {/* Desktop Sidebar */}
        <div className="w-64 lg:block hidden flex-shrink-0">
          <div id="drawer-navigation" className="w-64 fixed left-0 top-0 bottom-0 bg-[#080f1e] z-10 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-blue-950/20 via-transparent to-indigo-950/10 pointer-events-none" />
            <div className="relative h-full"><SidebarContent /></div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col min-h-screen">
            {/* Top Navbar */}
            <header className="bg-white/90 backdrop-blur-md border-b border-slate-200/60 py-3 px-5 lg:px-8 sticky top-0 z-20 flex items-center justify-between shadow-sm shadow-slate-200/50">
              <div className="flex items-center gap-3">
                <button className="text-slate-500 lg:hidden p-2 hover:bg-slate-100 rounded-xl transition-colors" onClick={handleToggle}>
                  <Icon icon="solar:hamburger-menu-bold-duotone" className="w-5 h-5" />
                </button>
                <button
                  onClick={() => navigate(-1)}
                  className="bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 w-8 h-8 flex items-center justify-center rounded-xl transition-all flex-shrink-0"
                >
                  <Icon icon="solar:arrow-left-bold" className="w-4 h-4" />
                </button>
                <div className="hidden sm:flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                  </span>
                  <span className="text-sm font-bold text-slate-700 tracking-tight">Super Admin Portal</span>
                  <span className="hidden md:block text-slate-300 text-xs">|</span>
                  <span className="hidden md:block text-xs text-slate-400 font-medium">Caresee Health Platform</span>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {/* Notification bell */}
                <div ref={notifRef} className="relative">
                  <button
                    onClick={notifOpen ? () => setNotifOpen(false) : openNotifPanel}
                    className={`relative w-9 h-9 flex items-center justify-center rounded-xl transition-all ${notifOpen ? "bg-slate-200 text-slate-700" : "bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700"}`}
                    title="Notifications"
                  >
                    <Icon icon="solar:bell-bold-duotone" className="w-5 h-5" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-rose-500 rounded-full border-2 border-white text-white text-[9px] font-black flex items-center justify-center px-0.5">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </button>
                  {notifOpen && <NotificationPanel />}
                </div>

                {/* Settings */}
                <div ref={settingsRef} className="relative">
                  <button
                    onClick={openSettingsPanel}
                    className={`w-9 h-9 flex items-center justify-center rounded-xl transition-all ${settingsOpen ? "bg-slate-200 text-slate-700" : "bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700"}`}
                    title="Settings"
                  >
                    <Icon icon={settings.autoRefresh ? "solar:settings-minimalistic-bold-duotone" : "solar:settings-bold-duotone"} className="w-5 h-5" />
                    {settings.autoRefresh && (
                      <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 rounded-full border-2 border-white" />
                    )}
                  </button>
                  {settingsOpen && <SettingsPanel />}
                </div>

                {/* Divider */}
                <div className="w-px h-6 bg-slate-200" />

                {/* User */}
                <div className="flex items-center gap-2.5">
                  <div className="text-right hidden sm:block">
                    <p className="text-[13px] font-bold text-slate-800 leading-tight">{userInfo?.name || "Super Admin"}</p>
                    <p className="text-[10px] text-cyan-600 font-semibold uppercase tracking-wider">Owner</p>
                  </div>
                  <div className="relative">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white font-black flex items-center justify-center text-sm shadow-md shadow-indigo-500/25">
                      {(userInfo?.name || "SA").substring(0, 2).toUpperCase()}
                    </div>
                    <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-500 border-2 border-white rounded-full" />
                  </div>
                </div>
              </div>
            </header>

            {/* Page Content */}
            <main className={`flex-1 bg-[#f6f8fc] ${settings.compactMode ? "p-4 lg:p-5" : "p-5 lg:p-8"}`}>
              <Outlet />
            </main>
          </div>

          {/* Mobile Sidenav */}
          <div ref={navWrapper} className="lg:hidden">
            <div
              className={`fixed inset-0 bg-black/70 backdrop-blur-sm z-30 transition-opacity duration-300 ${issideNavOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
              onClick={handleToggle}
            />
            <div
              className={`fixed top-0 bottom-0 left-0 w-72 bg-[#080f1e] z-40 transition-transform duration-300 ease-out overflow-hidden ${issideNavOpen ? "translate-x-0" : "-translate-x-full"}`}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-blue-950/20 via-transparent to-indigo-950/10 pointer-events-none" />
              <div className="absolute top-4 right-4 z-50">
                <button onClick={handleToggle} className="text-slate-500 hover:text-white p-2 hover:bg-white/10 rounded-xl transition-colors">
                  <Icon icon="solar:close-square-bold-duotone" className="w-5 h-5" />
                </button>
              </div>
              <div className="relative h-full"><SidebarContent /></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SuperAdminDashboard;
